import React, { useState, useEffect } from 'react';
import { API_LINK } from '../../apiLink/apiLink';
import Layout from '../../components/Layout';

const ParentFeesStatus = ({ onNavigate, userInfo }) => {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'history', 'breakdown'

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [parentData, setParentData] = useState({
    name: "Loading...",
    parentId: "...",
    relation: "Parent",
    profileImage: null
  });

  const [studentData, setStudentData] = useState({
    name: "Loading...",
    studentId: "...",
    rollNumber: "...",
    course: "...",
    department: "...",
    year: "...",
    batch: "..."
  });

  const [feesData, setFeesData] = useState({
    summary: {
      totalFeeAmount: "0.00",
      totalPaidAmount: "0.00",
      totalDueAmount: "0.00",
      totalScholarshipAmount: "0.00",
      hasPendingFees: false,
      paymentCompletionPercentage: 0
    },
    semesterWiseFees: [],
    paymentHistory: [],
    paymentTrends: []
  });

  // Fetch Fees Data from API
  useEffect(() => {
    fetchFeesData();
  }, [userInfo]);

  const fetchFeesData = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('🔍 Starting API call to fetch fees data...');

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');

      if (!token) {
        console.log('❌ No token found, using fallback data');
        setError('Authentication token not found. Please login again.');
        setLoading(false);
        return;
      }

      // Fetch parent profile data first for sidebar
      const profileUrl = `${API_LINK}/api/parent/profile`;
      console.log('🌐 Profile API URL:', profileUrl);

      const profileResponse = await fetch(profileUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Profile Response status:', profileResponse.status);

      if (profileResponse.ok) {
        const profileResult = await profileResponse.json();
        console.log('📦 Profile API Response:', profileResult);

        if (profileResult.success && profileResult.data) {
          const data = profileResult.data;
          console.log('✅ Processing profile API data...');

          // Update parent data
          setParentData({
            name: data.name || "Parent",
            parentId: data.parentId || "PARENT-001",
            relation: data.relation || "Parent",
            profileImage: data.profileImage || null
          });
        }
      }

      // Fetch fees data
      const feesUrl = `${API_LINK}/api/parent/student/fees`;
      console.log('🌐 Fees API URL:', feesUrl);

      const feesResponse = await fetch(feesUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 Fees Response status:', feesResponse.status);

      if (!feesResponse.ok) {
        if (feesResponse.status === 401) {
          console.log('🔐 Authentication failed');
          setError('Session expired. Please login again.');
          handleLogout();
          return;
        } else if (feesResponse.status === 404) {
          console.log('🔍 API endpoint not found');
          setError('Fees data endpoint not found. Please check server configuration.');
          setLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${feesResponse.status}`);
      }

      const feesResult = await feesResponse.json();
      console.log('📦 Fees API Response:', feesResult);

      if (feesResult.success && feesResult.data) {
        const data = feesResult.data;
        console.log('✅ Processing fees API data...');

        // Update student data
        setStudentData({
          name: data.student?.name || "Student",
          studentId: data.student?.studentId || "STU-001",
          rollNumber: data.student?.rollNumber || "...",
          course: data.student?.course || "...",
          department: data.student?.department || "Computer Science",
          year: data.student?.year || "3rd Year",
          batch: data.student?.batch || "2022-2025"
        });

        // Update fees data
        setFeesData({
          summary: data.summary || {
            totalFeeAmount: "0.00",
            totalPaidAmount: "0.00",
            totalDueAmount: "0.00",
            totalScholarshipAmount: "0.00",
            hasPendingFees: false,
            paymentCompletionPercentage: 0
          },
          semesterWiseFees: data.semesterWiseFees || [],
          paymentHistory: data.paymentHistory || [],
          paymentTrends: data.paymentTrends || []
        });

        console.log('✅ Fees data updated successfully');

      } else {
        console.log('❌ Fees API returned error:', feesResult.message);
        setError(feesResult.message || 'Failed to fetch fees data');
      }

    } catch (error) {
      console.error('💥 Error fetching fees data:', error);

      if (error.name === 'SyntaxError' && error.message.includes('Unexpected token')) {
        setError('Server returned HTML instead of JSON. Please check if the API endpoint exists.');
      } else if (error.message.includes('fetch')) {
        setError('Cannot connect to server. Please ensure the server is running');
      } else {
        setError(`Failed to load fees data: ${error.message}`);
      }

      if (userInfo) {
        console.log('🔄 Using fallback userInfo data');
        setParentData(prev => ({
          ...prev,
          name: userInfo.name || prev.name,
          parentId: userInfo.id || prev.parentId
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    console.log('🚪 Logging out...');
    sessionStorage.clear();
    localStorage.clear();

    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Handle navigation
  const handleNavigation = (page) => {
    switch (page) {
      case 'Dashboard':
        onNavigate('parent-dashboard');
        break;
      case 'Academic Performance':
        onNavigate('parent-academic-performance');
        break;
      case 'Fee Status':
        // Stay on current page
        break;
      case 'Holidays & Events':
        onNavigate('parent-holidays');
        break;
      case 'Emergency Alert':
        onNavigate('parent-emergency-alert');
        break;
      case 'Logout':
        handleLogout();
        break;
      default:
        console.log(`Navigation to ${page} clicked`);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  // Get fee status color
  const getFeeStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return { backgroundColor: '#dcfce7', color: '#166534', border: '#22c55e' };
      case 'partial':
        return { backgroundColor: '#fef3c7', color: '#92400e', border: '#fbbf24' };
      case 'due':
        return { backgroundColor: '#fee2e2', color: '#dc2626', border: '#ef4444' };
      default:
        return { backgroundColor: '#f3f4f6', color: '#6b7280', border: '#d1d5db' };
    }
  };

  // Get payment mode icon
  const getPaymentModeIcon = (mode) => {
    switch (mode?.toLowerCase()) {
      case 'cash':
        return '💵';
      case 'online':
      case 'upi':
        return '💳';
      case 'cheque':
        return '🏦';
      case 'bank transfer':
        return '🏛️';
      default:
        return '💰';
    }
  };

  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Fees Status...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem',
        padding: '2rem'
      }}>
        <div style={{
          backgroundColor: '#ffffff',
          padding: '3rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          maxWidth: '400px',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            fontSize: '4rem',
            color: '#ff6b6b',
            marginBottom: '1.5rem'
          }}>
            ⚠️
          </div>

          <h3 style={{
            color: '#333',
            margin: '0 0 1rem 0',
            fontSize: '1.4rem',
            fontWeight: 600
          }}>
            Unable to Load Fees Status
          </h3>

          <p style={{
            color: '#666',
            margin: '0 0 2rem 0',
            fontSize: '1rem',
            lineHeight: 1.5
          }}>
            {error}
          </p>

          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                backgroundColor: '#238548',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1e7139';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#238548';
              }}
            >
              Try Again
            </button>

            <button
              onClick={handleLogout}
              style={{
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 500,
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#555';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#666';
              }}
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Layout role="parent"
      onNavigate={handleNavigation}
      parentData={parentData}
      activePage="Fee Status"
      pageTitle="Fee Status"
    >
      {/* Student Info Header */}
      <section style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h2 style={{
          color: '#333',
          display: 'flex',
          alignItems: 'center',
          gap: '0.8rem',
          marginBottom: '1.5rem',
          fontSize: '2rem'
        }}>
          <span style={{ color: '#238548' }}>💳</span>
          Fee Status - {studentData.name}
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem'
        }}>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Student ID:</strong> {studentData.studentId}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Roll Number:</strong> {studentData.rollNumber}</p>
          </div>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Course:</strong> {studentData.course}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Department:</strong> {studentData.department}</p>
          </div>
          <div>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Year:</strong> {studentData.year}</p>
            <p style={{ margin: '0 0 0.5rem 0', color: '#666' }}><strong>Batch:</strong> {studentData.batch}</p>
          </div>
        </div>
      </section>

      {/* Fee Summary Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {[
          {
            icon: '💰',
            title: 'Total Fee Amount',
            value: formatCurrency(feesData.summary.totalFeeAmount),
            subtitle: 'Academic Year Total',
            color: '#3b82f6'
          },
          {
            icon: '✅',
            title: 'Amount Paid',
            value: formatCurrency(feesData.summary.totalPaidAmount),
            subtitle: `${feesData.summary.paymentCompletionPercentage}% Completed`,
            color: '#22c55e'
          },
          {
            icon: '⏰',
            title: 'Amount Due',
            value: formatCurrency(feesData.summary.totalDueAmount),
            subtitle: feesData.summary.hasPendingFees ? 'Payment Required' : 'No Pending Dues',
            color: feesData.summary.hasPendingFees ? '#ef4444' : '#22c55e'
          },
          {
            icon: '🎓',
            title: 'Scholarship Amount',
            value: formatCurrency(feesData.summary.totalScholarshipAmount),
            subtitle: 'Total Scholarship',
            color: '#8b5cf6'
          }
        ].map((stat, index) => (
          <div key={index} style={{
            background: '#ffffff',
            padding: '2rem',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            textAlign: 'center',
            transition: 'all 0.3s ease',
            border: `2px solid ${stat.color}20`
          }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-5px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
            }}>
            <div style={{
              fontSize: '2.5rem',
              marginBottom: '1rem'
            }}>
              {stat.icon}
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>{stat.title}</h3>
            <p style={{
              fontSize: '2rem',
              fontWeight: 'bold',
              color: stat.color,
              margin: '0.5rem 0'
            }}>
              {stat.value}
            </p>
            <p style={{ color: '#666', margin: 0, fontSize: '0.9rem' }}>
              {stat.subtitle}
            </p>
          </div>
        ))}
      </section>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        borderBottom: '1px solid #e0e0e0',
        paddingBottom: '0.5rem'
      }}>
        {[
          { id: 'overview', label: 'Detailed Breakdown' },
          { id: 'history', label: 'Payment History' }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'none',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #238548' : '3px solid transparent',
              color: activeTab === tab.id ? '#238548' : '#666',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              cursor: 'pointer',
              fontSize: '1rem',
              transition: 'all 0.3s ease'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Detailed Breakdown Tab */}
      {activeTab === 'overview' && (
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          <div style={{ padding: '1.5rem' }}>
            {feesData.semesterWiseFees.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                {feesData.semesterWiseFees.map((semester, index) => (
                  <div key={index} style={{
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      background: '#f8fafc',
                      padding: '1rem 1.5rem',
                      borderBottom: '1px solid #e0e0e0',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      flexWrap: 'wrap',
                      gap: '1rem'
                    }}>
                      <h4 style={{ margin: 0, color: '#333', fontSize: '1.1rem' }}>
                        {semester.semester}
                      </h4>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '20px',
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        border: '1px solid',
                        ...getFeeStatusColor(semester.status)
                      }}>
                        {semester.status.toUpperCase()}
                      </div>
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ background: '#ffffff', borderBottom: '1px solid #ecf0f1' }}>
                            <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Fee Description</th>
                            <th style={{ padding: '1rem 1.5rem', textAlign: 'right', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Amount</th>
                            <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Due Date</th>
                            <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#666', fontSize: '0.9rem' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {semester.fees.map((fee, feeIndex) => (
                            <tr key={feeIndex} style={{
                              borderBottom: feeIndex < semester.fees.length - 1 ? '1px solid #ecf0f1' : 'none',
                              backgroundColor: feeIndex % 2 === 0 ? '#ffffff' : '#fcfcfc'
                            }}>
                              <td style={{ padding: '1rem 1.5rem', color: '#333' }}>{fee.description}</td>
                              <td style={{ padding: '1rem 1.5rem', color: '#333', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(fee.amount)}</td>
                              <td style={{ padding: '1rem 1.5rem', color: '#666' }}>{fee.dueDate}</td>
                              <td style={{ padding: '1rem 1.5rem' }}>
                                <span style={{
                                  display: 'inline-block',
                                  padding: '0.25rem 0.5rem',
                                  borderRadius: '4px',
                                  fontSize: '0.8rem',
                                  fontWeight: 500,
                                  ...getFeeStatusColor(fee.status)
                                }}>
                                  {fee.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr style={{ background: '#f8fafc', borderTop: '2px solid #e0e0e0' }}>
                            <td style={{ padding: '1rem 1.5rem', fontWeight: 'bold', color: '#333' }}>Total</td>
                            <td style={{ padding: '1rem 1.5rem', fontWeight: 'bold', color: '#333', textAlign: 'right' }}>{formatCurrency(semester.totalAmount)}</td>
                            <td colSpan={2}></td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <p style={{ color: '#999', margin: 0 }}>No detailed fee breakdown available.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Payment History Tab */}
      {activeTab === 'history' && (
        <section style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem'
        }}>
          <div style={{ padding: '1.5rem' }}>
            {feesData.paymentHistory.length > 0 ? (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#333' }}>Date</th>
                      <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#333' }}>Receipt No.</th>
                      <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#333' }}>Description</th>
                      <th style={{ padding: '1rem 1.5rem', textAlign: 'left', fontWeight: 600, color: '#333' }}>Payment Mode</th>
                      <th style={{ padding: '1rem 1.5rem', textAlign: 'right', fontWeight: 600, color: '#333' }}>Amount Paid</th>
                      <th style={{ padding: '1rem 1.5rem', textAlign: 'center', fontWeight: 600, color: '#333' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {feesData.paymentHistory.map((payment, index) => (
                      <tr key={index} style={{
                        borderBottom: '1px solid #ecf0f1',
                        transition: 'background-color 0.2s ease'
                      }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '1rem 1.5rem', color: '#666' }}>{payment.date}</td>
                        <td style={{ padding: '1rem 1.5rem', color: '#333', fontFamily: 'monospace' }}>{payment.receiptNo}</td>
                        <td style={{ padding: '1rem 1.5rem', color: '#333' }}>{payment.description}</td>
                        <td style={{ padding: '1rem 1.5rem', color: '#333' }}>
                          <span style={{ marginRight: '0.5rem' }}>{getPaymentModeIcon(payment.mode)}</span>
                          {payment.mode}
                        </td>
                        <td style={{ padding: '1rem 1.5rem', color: '#238548', fontWeight: 600, textAlign: 'right' }}>{formatCurrency(payment.amount)}</td>
                        <td style={{ padding: '1rem 1.5rem', textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '0.25rem 0.75rem',
                            borderRadius: '20px',
                            fontSize: '0.85rem',
                            backgroundColor: '#dcfce7',
                            color: '#166534',
                            fontWeight: 500
                          }}>
                            Success
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }}>🧾</div>
                <h3 style={{ color: '#666', marginBottom: '0.5rem' }}>No Payment Records</h3>
                <p style={{ color: '#999', margin: 0 }}>No transaction history available yet.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ParentFeesStatus;